import React, { useState, useEffect } from 'react';
import { usePrinterService } from '../../hooks/usePrinterService';

const BLE_DISABLED = true;

export const PrinterSelectionModal = ({ isOpen, onClose, onSelect }) => {
  const { discoverDevices, connect } = usePrinterService();
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [transport, setTransport] = useState('auto');
  const [statusMessage, setStatusMessage] = useState('');

  const transportLabels = {
    auto: 'Bluetooth Classic',
    classic: 'Bluetooth Classic',
    ble: BLE_DISABLED ? 'Bluetooth Low Energy (disabled)' : 'Bluetooth Low Energy',
  };

  useEffect(() => {
    if (isOpen) {
      handleDiscover(transport);
    }
  }, [isOpen]);

  const handleDiscover = async (mode = 'auto') => {
    setLoading(true);
    setError(null);
    setTransport(mode);
    setStatusMessage(`Scanning for ${transportLabels[mode] || 'Bluetooth'} devices…`);
    try {
      const result = await discoverDevices({
        transport: mode === 'auto' ? undefined : mode,
      });
      if (result.success) {
        setPrinters(result.devices || []);
      } else {
        setError(result.message || 'Failed to discover printers');
      }
    } catch (err) {
      setError(err.message || 'Failed to discover printers');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (printer) => {
    setConnecting(true);
    setError(null);
    try {
      const result = await connect(printer);
      if (result.success) {
        onSelect?.(result.device);
        onClose();
      } else {
        setError(result.message || 'Failed to connect to printer');
      }
    } catch (err) {
      setError(err.message || 'Failed to connect to printer');
    } finally {
      setConnecting(false);
    }
  };

  const getTransportIcon = (transport) => {
    if (transport === 'bluetooth') return '📶';
    if (transport === 'usb') return '🔌';
    return '🖨️';
  };

  const getTransportLabel = (transport) => {
    if (transport === 'bluetooth') return 'Bluetooth';
    if (transport === 'usb') return 'USB';
    return transport || 'Unknown';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-green-200 bg-green-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Select Printer</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">{statusMessage || 'Discovering printers…'}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { label: 'Classic', value: 'auto' },
              { label: 'BLE', value: 'ble', disabled: BLE_DISABLED },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleDiscover(option.value)}
                disabled={option.disabled || (loading && transport === option.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  option.disabled
                    ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                    : transport === option.value
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {BLE_DISABLED && (
            <p className="text-xs text-amber-600 mb-3">
              BLE scanning is temporarily disabled. Please use Bluetooth Classic.
            </p>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {!loading && printers.length === 0 && !error && (
            <div className="text-center py-8 text-sm text-gray-600">
              No printers found. Ensure Bluetooth is on, Nearby Devices permission is granted, and the printer is powered.
            </div>
          )}

          {!loading && printers.length > 0 && (
            <div className="space-y-2">
              {printers.map((printer) => (
                <button
                  key={printer.id}
                  onClick={() => handleSelect(printer)}
                  disabled={connecting}
                  className="w-full text-left p-3 border border-green-200 rounded-md hover:bg-green-50 hover:border-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{printer.name || 'Unknown Printer'}</div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                        <span>{getTransportIcon(printer.transport)}</span>
                        <span>{getTransportLabel(printer.transport)}</span>
                        {printer.isDefault && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">Default</span>
                        )}
                      </div>
                    </div>
                    {connecting && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 ml-2"></div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-green-200 bg-gray-50">
          <button
            onClick={() => handleDiscover(transport)}
            disabled={loading}
            className="w-full px-4 py-2 text-sm bg-white border border-green-300 text-green-700 rounded-md hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Scanning...' : 'Refresh List'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrinterSelectionModal;

